import React, { useEffect, useMemo, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../config/firebase";
import { useTasks } from "../context/TaskContext";
import { getTelemetryEventsCollectionPath } from "../services/firestorePaths";
import { isTelemetryEnabled } from "../services/telemetryStore";

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return String(value);
  }
};

const DevTelemetry = () => {
  const { user, activeListId, telemetryEnabled } = useTasks();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.uid || !activeListId) return undefined;

    const eventsRef = collection(
      db,
      getTelemetryEventsCollectionPath(user.uid, activeListId)
    );
    const q = query(eventsRef, orderBy("timestamp", "desc"), limit(200));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nextEvents = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setEvents(nextEvents);
        setError("");
      },
      (err) => {
        console.error("Failed to load telemetry events", err);
        setError(err.message || "Failed to load telemetry events");
      }
    );

    return unsubscribe;
  }, [activeListId, user?.uid]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return events;
    return events.filter((event) => {
      const haystack = [
        event.type,
        event.attemptId,
        event.problemId,
        event.taskId,
        event.fsmEventType,
        event.stateFrom,
        event.stateTo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [events, filter]);

  const enabled = telemetryEnabled || isTelemetryEnabled();

  return (
    <div style={{ padding: "20px", maxWidth: "1100px", margin: "0 auto" }}>
      <div className="page-section__headline">
        <div>
          <h2 style={{ margin: 0 }}>Telemetry (dev)</h2>
          <p className="mini-label" style={{ margin: 0 }}>
            {enabled
              ? `Showing last ${events.length} events for list ${activeListId || "—"}.`
              : "Telemetry disabled (set REACT_APP_TELEMETRY_ENABLED=true to persist events)."}
          </p>
        </div>
      </div>

      {error ? (
        <p className="mini-label" style={{ color: "#b00020" }}>
          {error}
        </p>
      ) : null}

      <div className="task-form__row" style={{ marginTop: "12px" }}>
        <div className="task-form__field" style={{ maxWidth: "420px" }}>
          <label>Filter (attemptId / problemId / type)</label>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="e.g. attempt_fsm_transition"
          />
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="table" style={{ width: "100%", marginTop: "12px" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Time</th>
              <th style={{ textAlign: "left" }}>Type</th>
              <th style={{ textAlign: "left" }}>Attempt</th>
              <th style={{ textAlign: "left" }}>Problem</th>
              <th style={{ textAlign: "left" }}>FSM</th>
              <th style={{ textAlign: "left" }}>Data</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((event) => (
              <tr key={event.id}>
                <td style={{ whiteSpace: "nowrap" }}>
                  {formatTimestamp(event.timestamp)}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>{event.type || "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {event.attemptId || "—"}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>{event.problemId || "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {event.fsmEventType
                    ? `${event.stateFrom || "?"} → ${event.stateTo || "?"}`
                    : "—"}
                </td>
                <td style={{ maxWidth: "460px" }}>
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                    {JSON.stringify(event.data || null)}
                  </pre>
                </td>
              </tr>
            ))}
            {!filtered.length ? (
              <tr>
                <td colSpan={6} className="mini-label">
                  No events yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DevTelemetry;

